<template>
  <div
    v-if="settings"
    class="flex flex-col max-w-2xl mt-20 md:items-center md:mx-auto md:justify-center"
  >
    <h1
      class="font-sans text-xl font-semibold text-center md:text-center md:font-bold md:text-3xl"
    >
      {{ settings.title }}
    </h1>
    <p class="mt-5 text-gray-500 md:text-center">
      {{ settings.sub_title }}
    </p>
  </div>
  <div
    class="max-w-5xl mx-auto mt-5 md:px-0"
    v-if="listings && Array.isArray(listings.data) && listings.data.length"
  >
    <header class="flex items-center justify-between mb-4">
      <p class="mt-2 text-gray-500">
        Discover how we can help you find your dream home
      </p>
    </header>
    <div class="mx-4">
      <vue-horizontal
        responsive
        class="horizontal"
        :displacement="0.7"
        snap="center"
      >
        <div class="flex space-x-1">
          <div v-for="(property, index) in listings.data" :key="property.id">
            <div v-if="index % 3 === 0" class="flex space-x-3">
              <!-- Two stacked items -->
              <div class="flex flex-col space-y-3 w-[310px]">
                <!-- First stacked item -->
                <a
                  :href="
                    $router.resolve({
                      name: 'PropertyView',
                      params: {
                        id: property.id,
                        slug: property.title
                          ? $sanitizeTitle(property.title)
                          : '',
                      },
                    }).href
                  "
                  class="flex flex-col items-start w-full h-[192px]"
                >
                  <div
                    class="relative w-full h-full overflow-hidden rounded-md"
                  >
                    <img
                      :src="property.photos[0]"
                      class="object-cover w-full h-full transition duration-500 rounded-md cursor-pointer hover:scale-110"
                      alt=""
                    />
                    <div class="absolute top-0 left-0 flex mt-2 ml-2 space-x-1">
                      <p
                        class="text-2xl font-medium tracking-wide text-white"
                        style="text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5)"
                      >
                        {{ property.region.name }}
                      </p>
                    </div>
                    <div class="absolute bottom-0 left-0 mb-2 ml-2">
                      <button
                        class="flex items-center justify-center px-4 py-1 space-x-2 text-black bg-white rounded-md bg-opacity-70"
                      >
                        View Homes
                        <div class="w-4 h-4">
                          <svg
                            role="img"
                            aria-hidden="true"
                            class="svg"
                            viewBox="0 0 32 32"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M17.65 16.513l-7.147-7.055 1.868-1.893 9.068 8.951-9.069 8.927-1.866-1.896z"
                              fill="#000"
                            ></path>
                          </svg>
                        </div>
                      </button>
                    </div>
                  </div>
                </a>

                <!-- Second stacked item -->
                <a
                  v-if="listings.data[index + 1]"
                  :href="
                    $router.resolve({
                      name: 'PropertyView',
                      params: {
                        id: listings.data[index + 1].id,
                        slug: listings.data[index + 1].title
                          ? $sanitizeTitle(listings.data[index + 1].title)
                          : '',
                      },
                    }).href
                  "
                  class="flex flex-col items-start w-full h-[192px]"
                >
                  <div
                    class="relative w-full h-full overflow-hidden rounded-md"
                  >
                    <img
                      :src="listings.data[index + 1]?.photos[0]"
                      class="object-cover w-full h-full transition duration-500 rounded-md cursor-pointer hover:scale-110"
                      alt=""
                    />
                    <div class="absolute top-0 left-0 flex mt-2 ml-2 space-x-1">
                      <p
                        class="text-2xl font-medium tracking-wide text-white"
                        style="text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5)"
                      >
                        {{ listings.data[index + 1]?.region.name }}
                      </p>
                    </div>
                    <div class="absolute bottom-0 left-0 mb-2 ml-2">
                      <button
                        class="flex items-center justify-center px-4 py-1 space-x-2 text-black bg-white rounded-md bg-opacity-70"
                      >
                        View Homes
                        <div class="w-4 h-4">
                          <svg
                            role="img"
                            aria-hidden="true"
                            class="svg"
                            viewBox="0 0 32 32"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M17.65 16.513l-7.147-7.055 1.868-1.893 9.068 8.951-9.069 8.927-1.866-1.896z"
                              fill="#000"
                            ></path>
                          </svg>
                        </div>
                      </button>
                    </div>
                  </div>
                </a>
              </div>

              <!-- Full-length item beside stacked items -->
              <a
                v-if="listings.data[index + 2]"
                :href="
                  $router.resolve({
                    name: 'PropertyView',
                    params: {
                      id: listings.data[index + 2].id,
                      slug: listings.data[index + 2].title
                        ? $sanitizeTitle(listings.data[index + 2].title)
                        : '',
                    },
                  }).href
                "
                class="flex flex-col items-start w-[310px] h-[397px] ml-3"
              >
                <div class="relative w-full h-full overflow-hidden rounded-md">
                  <img
                    :src="listings.data[index + 2].photos[0]"
                    class="object-cover w-full h-full transition duration-500 rounded-md cursor-pointer hover:scale-110"
                    alt=""
                  />
                  <div class="absolute top-0 left-0 flex mt-2 ml-2 space-x-1">
                    <p
                      class="text-2xl font-medium tracking-wide text-white"
                      style="text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5)"
                    >
                      {{ listings.data[index + 2].region.name }}
                    </p>
                  </div>
                  <div class="absolute bottom-0 left-0 mb-2 ml-2">
                    <button
                      class="flex items-center justify-center px-4 py-1 space-x-2 text-black bg-white rounded-md bg-opacity-70"
                    >
                      View Homes
                      <div class="w-4 h-4">
                        <svg
                          role="img"
                          aria-hidden="true"
                          class="svg"
                          viewBox="0 0 32 32"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.65 16.513l-7.147-7.055 1.868-1.893 9.068 8.951-9.069 8.927-1.866-1.896z"
                            fill="#000"
                          ></path>
                        </svg>
                      </div>
                    </button>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </vue-horizontal>
    </div>
  </div>
</template>

<script>
import VueHorizontal from "vue-horizontal";
export default {
  components: {
    VueHorizontal,
  },
  props: {
    listings: Object,
    settings: Object,
  },
  watch: {
    listings: {
      immediate: true,
    },
  },
};
</script>

<style scoped>
header {
  @apply mb-4 flex justify-between items-center;
}

nav > button {
  @apply p-1 rounded-sm outline-none;
}

nav > button.active {
  @apply text-gray-700;
}

nav > button.inactive {
  @apply text-gray-300;
}

nav > button:focus {
  @apply outline-none;
}

nav > button > svg {
  @apply md:h-6 md:w-6 h-4 w-4 fill-current;
}
</style>
